import React, { useState, useRef, useEffect } from "react";
import { toast } from "react-toastify";
import { Navigate, useNavigate } from "react-router-dom";
import {
  LoginAPi,
} from "../actioncreators/actioncreators";
import "../../App.css";
import ArthaTRACK from "../Assets/LOGO.png";
import LoginPng from "../Assets/login.png";
import { useDispatch } from "react-redux";
import { emailId } from "../actions/loanAction";
import { Mail, ArrowRight, MapPin, Users, Clock } from "lucide-react";

const LoginForm = () => {
  const [email, setEmail] = useState("");
  const [isButtonEnabled, setIsButtonEnabled] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const handleInputChange = (e) => {
    const enteredEmail = e.target.value;
    setEmail(enteredEmail);

    // Enable the button if an email is entered
    setIsButtonEnabled(enteredEmail.trim().length > 0);
  };

  const handleLoginClick = async (e) => {
    e.preventDefault(); // Prevent form submission reload

    if (isButtonEnabled) {
      setIsLoading(true);
      const requestBody = {
        email: email, // Only email is being sent
        ui: true,
      };

      try {
        const response = await LoginAPi(requestBody);

        if (response.status === 200) {
          const responseData = response.data;
          console.log(responseData);

          if (responseData.businessStatusCode === 2) {
            if (responseData.message === "OTP has been sent") {
              // localStorage.removeItem("timerStartTime");
              dispatch(emailId(responseData.emailId));
              toast.success(response.message || "OTP has been sent");

              navigate("/getOtp");
            } else {
              toast.info(response.message);
            }
          } else {
            // Handle user not registered or other error scenarios
            if (responseData.message) {
              toast.error(responseData.message);
            } else {
              toast.error("User is not registered. Please create an account.");
            }
          }
        } else {
          console.log("Unexpected response status:", response.status);
        }
      } catch (error) {
        console.error("Login failed:", error);
        console.error("Error fetching loan data:", error);

        // Handle error responses
        if (error.response?.data) {
          const errorData = error.response.data;
          if (errorData.message) {
            toast.error(errorData.message);
          } else if (errorData.errorCode) {
            toast.error(`Error ${errorData.errorCode}: ${errorData.message || 'Something went wrong'}`);
          }
        } else {
          toast.error("Unable to connect. Please try again.");
        }
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <div className="row p-0 m-0 login-page-container">
      {/* Animated gradient background header */}
      <header className="col-12 shadow-sm login-header login-header-gradient">
        <div className="px-3 py-2 d-flex align-items-center justify-content-between">
          <img src={ArthaTRACK} alt="ArthaTRACK" className="login-logo login-logo-animated" />
          <div className="d-none d-md-flex gap-3 tracking-features">
            <div className="feature-badge">
              <MapPin size={14} />
              <span>GPS Tracking</span>
            </div>
            <div className="feature-badge">
              <Clock size={14} />
              <span>Live Time</span>
            </div>
            <div className="feature-badge">
              <Users size={14} />
              <span>Team Management</span>
            </div>
          </div>
        </div>
      </header>

      <main className="row m-0 login-main login-main-gradient">
        {/* Spacer - hidden on mobile */}
        <div className="d-none d-lg-block col-lg-1"></div>

        {/* Left Column - Illustration with floating animation */}
        <div className="d-none d-md-flex col-md-6 col-lg-5 bg-white rounded-lg text-center align-items-center justify-content-center shadow-lg mx-1 mx-lg-2 login-illustration-container illustration-card-animated">
          <div className="illustration-content">
            <div className="floating-animation">
              <img
                src={LoginPng}
                alt="ArthaTRACK"
                className="h-auto mx-auto object-contain login-illustration"
                style={{ maxWidth: "100%", width: "auto", maxHeight: "280px" }}
              />
            </div>
            <p className="text-center custom-blue-text mt-3 px-2 tagline-animated">
              Streamline attendance, payroll, and compliance by easily tracking when your team is working.
            </p>
            {/* Feature highlights */}
            <div className="features-grid">
              <div className="feature-item">
                <div className="feature-icon">
                  <MapPin size={18} />
                </div>
                <span className="feature-text">Real-time Location</span>
              </div>
              <div className="feature-item">
                <div className="feature-icon">
                  <Clock size={18} />
                </div>
                <span className="feature-text">Smart Timesheets</span>
              </div>
              <div className="feature-item">
                <div className="feature-icon">
                  <Users size={18} />
                </div>
                <span className="feature-text">Team Insights</span>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - Login Form with animations */}
        <div className="col-12 col-md-5 col-lg-4 bg-white rounded-lg shadow-lg mx-auto mx-md-1 mx-lg-2 text-center login-form-container form-card-animated">
          <div className="login-form-header">
            <h1 className="fw-bold text-center fs-4 fs-md-3 title-fade-in">Welcome Back!</h1>
            <h2 className="fs-6 fs-md-5 font-bold subtitle-fade-in" style={{ color: "#666" }}>Sign in to track your team</h2>
          </div>

          <form className="modern-form">
            <div className="input-group-animated">
              <label
                htmlFor="email"
                className="block text-sm font-medium text-start mb-2"
                style={{ color: "#374151", fontWeight: "600" }}
              >
                Email Address
              </label>
              <div className="modern-input-wrapper">
                <Mail className="input-icon" size={18} />
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={email}
                  onChange={handleInputChange}
                  onFocus={() => setIsFocused(true)}
                  onBlur={() => setIsFocused(false)}
                  className={`modern-input ${isFocused ? 'input-focused' : ''} ${email ? 'has-value' : ''}`}
                  required
                  placeholder="you@example.com"
                />
              </div>
            </div>

            <button
              type="button"
              onClick={handleLoginClick}
              disabled={!isButtonEnabled || isLoading}
              className="modern-login-btn"
            >
              {isLoading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Processing...
                </>
              ) : (
                <>
                  Continue
                  <ArrowRight className="btn-arrow" size={18} />
                </>
              )}
            </button>

            <div className="divider-container">
              <div className="divider-line"></div>
              <span className="divider-text">New to ArthaTRACK?</span>
              <div className="divider-line"></div>
            </div>

            <a href="/registration" className="create-account-link">
              <span>Create your account</span>
              <ArrowRight size={16} />
            </a>
          </form>

          {/* Trust indicators */}
          <div className="trust-indicators">
            <div className="trust-item">
              <div className="trust-checkmark">✓</div>
              <span>Secure & Encrypted</span>
            </div>
            <div className="trust-item">
              <div className="trust-checkmark">✓</div>
              <span>GDPR Compliant</span>
            </div>
          </div>
        </div>

        {/* Spacer - hidden on mobile */}
        <div className="d-none d-lg-block col-lg-1"></div>
      </main>

      {/* Floating particles background effect */}
      <div className="particles-background">
        <div className="particle particle-1"></div>
        <div className="particle particle-2"></div>
        <div className="particle particle-3"></div>
        <div className="particle particle-4"></div>
        <div className="particle particle-5"></div>
      </div>
    </div>
  );
};

export default LoginForm;
